import mori from 'mori';
import { appDefault } from '../../config/config';
import {
  cellRenderer,
  valueGetter,
} from './';
import { maxWordWidth } from '../../util/util';
import sortComparator from './sortComparator';

/**
 * Assemble the ag-grid header
 * @TODO finish building out this docblock
 * @param  {[type]} header           [description]
 * @param  {[type]} linkColumn       [description]
 * @param  {[type]} linkTarget       [description]
 * @param  {[type]} linkContext      Used when building the selectedRows tree
 *                                   for inter-indicator linking
 * @param  {[type]} timeframe        [description]
 * @param  {[type]} trend            [description]
 * @param  {[type]} baseDistribution [description]
 * @param  {[type]} mapBaseColumn    [description]
 * @param  {[type]} geoMulti         [description]
 * @param  {[type]} baseContext      [description]
 * @param  {[type]} selectedHeaders  [description]
 * @param  {[type]} headerGroupTitle The text displayed in the header group table cell.
 * @param  {[type]} currentGroup     The header group this header belongs to.
 * @return {[type]}                  [description]
 */
export default function(
  header,
  linkColumn,
  linkTarget,
  linkContext,
  timeframe,
  trend,
  baseDistribution,
  mapBaseColumn,
  geoMulti,
  baseContext,
  selectedHeaders = null,
  headerGroupTitle = null,
  currentGroup = null
) {
  const currentHeader = mori.toJs(header);
  const characteristic = currentHeader.characteristic;

  // align numeric columns right
  const numericColumn = ([
    'number',
    'percent',
    'currency',
    'year',
    'rate',
    'default', // Temporary Addition for a fix
  ].indexOf(currentHeader.type) > -1) ||
    currentHeader.Prefix ||
    currentHeader.Suffix;

  // Take into account space needed for sorting arrows in columns
  const colWidthAdjust = 35;

  const field = (trend ? `${mori.first(timeframe)}__` : '') + characteristic;

  // Font style used to more accurately estimate width of text in headers
  const fontStyleString = 'bold 14px/20px "Open Sans", helvetica, sans-serif';

  // For Grouped Headers, we need to accurately account for long words
  // in the header group cell.  We'll find the longest word and adjust
  // the widths of the header group children appropriately.

  // Find base width for the current header
  const baseHeaderWidth = appDefault.defaultWidths
  [currentHeader.type][currentHeader.width || 'default'] ||
    appDefault.defaultWidths
    [currentHeader.type].default;

  // Find the longest word in the group header
  const estHeaderGroupWidth = currentHeader.headerGroup ?
    maxWordWidth(
      currentHeader.headerGroup,
      fontStyleString
    ) + colWidthAdjust : 0;

  // Find the width of all children columns under a header group.
  // This is needed, to ensure we size children columns correctly.
  // Reduce the group of children columns down to a cummulative default width.
  const defaultHeaderGroupWidth = mori.reduce(
    (acc, item) =>
      acc + appDefault.defaultWidths
      [mori.get(item, 'type')][mori.get(item, 'width') || 'default'] ||
      appDefault.defaultWidths[mori.get(item, 'type')].default,
    0,
    currentGroup
  );

  // Get the proportion of the current header width to the group's width
  const headerWidthFactor = baseHeaderWidth / (defaultHeaderGroupWidth || 1);

  // If longest word in group header is wider than entire header group
  // compare which is wider:
  // 1.  The base header width plus a proporition of the width difference
  // 2.  The widest word in the current header
  const estWidth = Math.max(
    defaultHeaderGroupWidth > estHeaderGroupWidth ?
      0 : (
        baseHeaderWidth + (
          (estHeaderGroupWidth - defaultHeaderGroupWidth) * headerWidthFactor
        )
      ),
    maxWordWidth(characteristic, fontStyleString) + colWidthAdjust
  );

  const comparator = sortComparator(
    currentHeader,
    baseDistribution,
    mapBaseColumn
  );

  let agHeader = mori.hashMap(
    'cellRenderer', cellRenderer,
    'field', headerGroupTitle ? `${headerGroupTitle}__${field}` : field,
    'headerName', characteristic,
    'sortingOrder', appDefault.descSortFirst
      .indexOf(currentHeader.type) !== -1 ? ['desc', 'asc'] : ['asc', 'desc'],
    'cellStyle', () => {
      // Some times prefix/suffix get added to geo headers and appear "Numeric"
      return (numericColumn &&
        currentHeader.type !== 'geography' &&
        currentHeader.idx !== 0) ? { 'text-align': 'right' } : null;
    },
    'width', (estWidth > baseHeaderWidth ? estWidth : baseHeaderWidth),
    'cellRendererParams', mori.hashMap(
      'currentHeader', currentHeader,
      'linkTarget', linkTarget,
      'linkColumn', linkColumn,
      'linkContext', linkContext,
      'selectedHeaders', selectedHeaders,
      'geoMulti', geoMulti,
      'baseContext', baseContext,
    ),
    'comparator', comparator,
    'valueGetter', valueGetter
  );

  if (characteristic === baseDistribution) {
    agHeader = mori.assoc(
      agHeader,
      'pinned', 'left'
    );
  }

  if (characteristic === 'Location' ||
    (characteristic === 'State' && currentHeader.idx === 0)
  ) {
    agHeader = mori.assoc(
      mori.assoc(
        agHeader,
        'width', 160
      ),
      'suppressSizeToFit', true
    );
  }

  return agHeader;
}



/** WEBPACK FOOTER **
 ** ../~/eslint-loader!./js/services/table/buildHeader.js
 **/